import { useEffect, useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useTour } from '@/hooks/useTour';

interface TourStep {
  targetSelector?: string;
  title: string;
  body: string;
}

const STEPS: TourStep[] = [
  {
    title: 'Witamy w AIAds 2.0',
    body: 'Szybki 6-krokowy przewodnik po głównych funkcjach panelu.',
  },
  {
    targetSelector: 'aside nav',
    title: 'Menu boczne',
    body: 'Wszystkie moduły są dostępne z lewego paska nawigacji.',
  },
  {
    targetSelector: 'a[href="/insights"]',
    title: 'Centrum analiz',
    body: 'Health Score, anomalie, diagnostyka priorytetowa w jednym miejscu.',
  },
  {
    targetSelector: 'a[href="/autoopt"]',
    title: 'Auto-optymalizacja',
    body: 'Zostaw rutynowe optymalizacje AI — Ty zatwierdzasz zmiany.',
  },
  {
    targetSelector: 'a[href="/reports"]',
    title: 'Raporty',
    body: 'Generuj raporty PDF / CSV, planuj wysyłki mailowe.',
  },
  {
    targetSelector: 'a[href="/knowledge"]',
    title: 'Baza wiedzy',
    body: 'Copilot AI odpowiada na pytania w oparciu o własne artykuły.',
  },
];

/**
 * pwa_ — First-run in-app product tour. Self-contained overlay, no external
 * library. Renders only when the user has not completed the tour yet.
 * The backend marks completion via /user/tour-complete.
 */
export function ProductTour() {
  const { user } = useAuth();
  const tour = useTour();
  const [visible, setVisible] = useState(false);

  // Show the tour automatically for users who have never seen it.
  // `tour_completed_at` is persisted by the backend; legacy users may
  // not have the field yet so treat missing as not-completed.
  const tourCompletedAt = (user as unknown as { tour_completed_at?: string | null } | null)
    ?.tour_completed_at;

  useEffect(() => {
    if (!user) return;
    if (tourCompletedAt) return;
    if (!tour.active) {
      tour.start();
    }
    setVisible(true);
    // run once per user login — next/prev inside the tour are internal state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tourCompletedAt]);

  if (!user || tourCompletedAt || !visible) return null;

  const step = STEPS[Math.min(tour.currentStep, STEPS.length - 1)];
  const isLast = tour.currentStep >= STEPS.length - 1;
  const isFirst = tour.currentStep === 0;

  function handleSkip() {
    tour.skip();
    setVisible(false);
  }

  async function handleComplete() {
    await tour.complete();
    setVisible(false);
    // Refresh user so tour_completed_at is picked up next session.
    try {
      await useAuth.getState().refresh();
    } catch {
      // non-fatal
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Dim background */}
      <div className="absolute inset-0 bg-slate-900/40 pointer-events-auto" onClick={handleSkip} />

      {/* Card */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,440px)] pointer-events-auto">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Krok {tour.currentStep + 1} z {STEPS.length}
            </span>
            <button
              type="button"
              onClick={handleSkip}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
              aria-label="Pomiń przewodnik"
            >
              <X size={16} />
            </button>
          </div>
          <div className="px-5 py-5">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">
              {step.title}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {step.body}
            </p>
          </div>
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Pomiń
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={isFirst}
                onClick={() => tour.prev()}
                className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ArrowLeft size={12} /> Wstecz
              </button>
              {isLast ? (
                <button
                  type="button"
                  onClick={handleComplete}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700"
                >
                  <Check size={12} /> Zakończ
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => tour.next(STEPS.length)}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700"
                >
                  Dalej <ArrowRight size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
